/**
 * TanStack Query hooks for the Laboratory module.
 *
 * Two conventions this file follows throughout, both established by the
 * already-migrated screens:
 *
 *  * **Branch-scoped hooks append `branchVersion`.** `branchKey()` embeds the
 *    current branch id at render time, so a switch must force a re-render for
 *    the key to be recomputed — see queryKeys.ts's `invalidateBranchScoped`
 *    comment for the race this closes. Tenant-wide hooks (the masters, a
 *    patient's history) deliberately do NOT, because their data is identical
 *    at every branch and re-keying them would throw the cache away for
 *    nothing.
 *
 *  * **Mutations invalidate the queues they can change, not everything.** A
 *    collection changes the sample queues, the order it belongs to and the
 *    dashboard — but not the masters, and not another patient's history.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/lib/queryKeys';
import * as lab from './api';
import type { LabOrderFilters, LabSampleFilters, ResultQueueFilters } from './api';
import type {
  CreateLabOrderInput, RejectionReason, SaveResultInput, TranscriptionInput,
} from './types';

/**
 * branchKey() ends in the id it was built for; dropping that last segment gives
 * the prefix every entry of that family shares, so "all open outside-lab
 * details" can be invalidated without knowing which ids are cached.
 */
const branchPrefix = (key: readonly unknown[]) => key.slice(0, -1);

/**
 * Everything a workflow action can invalidate. Called by every mutation
 * below rather than each one listing its own set, because the statuses cascade
 * (collecting a sample moves its order and its tests too) and a hook that
 * invalidated only "its own" list would leave the screen next door stale.
 */
function invalidateWorkflow(qc: QueryClient, orderId?: number) {
  qc.invalidateQueries({ queryKey: keys.laboratory.dashboard() });
  qc.invalidateQueries({ queryKey: keys.laboratory.orders() });
  qc.invalidateQueries({ queryKey: keys.laboratory.samples() });
  qc.invalidateQueries({ queryKey: keys.laboratory.results() });
  qc.invalidateQueries({ queryKey: keys.laboratory.critical() });
  // A collection, a verification or a cancellation can move an outsourced
  // test too, so the outside worklist and its open detail are stale as well.
  qc.invalidateQueries({ queryKey: keys.laboratory.outsourced() });
  qc.invalidateQueries({ queryKey: branchPrefix(keys.laboratory.outsourcedItem(0)) });
  if (orderId) {
    qc.invalidateQueries({ queryKey: keys.laboratory.order(orderId) });
    qc.invalidateQueries({ queryKey: keys.laboratory.report(orderId) });
  }
}

// ── Dashboard & MIS ──────────────────────────────────────────────

export function useLabDashboard() {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.dashboard(), branchVersion],
    queryFn: lab.fetchLabDashboard,
    // A laboratory dashboard is a live work board; a minute-old count is
    // misleading when someone is deciding what to work on next.
    refetchInterval: 60000,
  });
}

export function useTatReport(from?: string, to?: string) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.tat(from, to), branchVersion],
    queryFn: () => lab.fetchTatReport(from, to),
  });
}
export function useTestWiseReport(from?: string, to?: string) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.testWise(from, to), branchVersion],
    queryFn: () => lab.fetchTestWiseReport(from, to),
  });
}
export function useLabRevenueReport(from?: string, to?: string) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.revenue(from, to), branchVersion],
    queryFn: () => lab.fetchLabRevenueReport(from, to),
  });
}
export function useRejectionReport(from?: string, to?: string) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.rejections(from, to), branchVersion],
    queryFn: () => lab.fetchRejectionReport(from, to),
  });
}

// ── Masters (tenant-wide — no branchVersion) ─────────────────────

export const useLabDepartments = () =>
  useQuery({ queryKey: keys.labMasters.departments(), queryFn: () => lab.fetchDepartments(), staleTime: 300000 });
export const useLabTestCategories = () =>
  useQuery({ queryKey: keys.labMasters.testCategories(), queryFn: lab.fetchTestCategories, staleTime: 300000 });
export const useLabSampleTypes = () =>
  useQuery({ queryKey: keys.labMasters.sampleTypes(), queryFn: lab.fetchSampleTypes, staleTime: 300000 });
export const useLabContainers = () =>
  useQuery({ queryKey: keys.labMasters.containers(), queryFn: lab.fetchContainers, staleTime: 300000 });

export function useLabTests(params: { q?: string; department_id?: number; include_inactive?: boolean; processing_type?: string } = {}) {
  return useQuery({
    queryKey: keys.labMasters.tests(params),
    queryFn: () => lab.fetchTests(params),
    staleTime: 60000,
  });
}

export function useLabTest(id: number | null) {
  return useQuery({
    queryKey: keys.labMasters.test(id ?? 0),
    queryFn: () => lab.fetchTest(id as number),
    enabled: id !== null,
  });
}

export const useLabPackages = () =>
  useQuery({ queryKey: keys.labMasters.packages(), queryFn: lab.fetchPackages, staleTime: 300000 });

/** One invalidator for every master mutation — they all feed the same pickers. */
function useMasterMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.labMasters.all() }); },
  });
}

export const useSaveDepartment = () =>
  useMasterMutation((a: { id?: number; body: Record<string, unknown> }) =>
    a.id ? lab.updateDepartment(a.id, a.body) : lab.createDepartment(a.body));
export const useSaveTestCategory = () =>
  useMasterMutation((a: { id?: number; body: Record<string, unknown> }) =>
    a.id ? lab.updateTestCategory(a.id, a.body) : lab.createTestCategory(a.body));
export const useSaveSampleType = () =>
  useMasterMutation((a: { id?: number; body: Record<string, unknown> }) =>
    a.id ? lab.updateSampleType(a.id, a.body) : lab.createSampleType(a.body));
export const useSaveContainer = () =>
  useMasterMutation((a: { id?: number; body: Record<string, unknown> }) =>
    a.id ? lab.updateContainer(a.id, a.body) : lab.createContainer(a.body));
export const useSaveTest = () =>
  useMasterMutation((a: { id?: number; body: Record<string, unknown> }) =>
    a.id ? lab.updateTest(a.id, a.body) : lab.createTest(a.body));
export const useSaveParameter = () =>
  useMasterMutation((a: { id?: number; testId?: number; body: Record<string, unknown> }) =>
    a.id ? lab.updateParameter(a.id, a.body) : lab.createParameter(a.testId as number, a.body));
export const useSaveReferenceRange = () =>
  useMasterMutation((a: { id?: number; parameterId?: number; body: Record<string, unknown> }) =>
    a.id ? lab.updateReferenceRange(a.id, a.body) : lab.createReferenceRange(a.parameterId as number, a.body));
export const useSavePackage = () =>
  useMasterMutation((a: { id?: number; body: Record<string, unknown> }) =>
    a.id
      ? lab.updatePackage(a.id, a.body)
      : lab.createPackage(a.body as unknown as lab.SaveLabPackageInput));

// ── Orders ───────────────────────────────────────────────────────

export function useLabOrders(filters: LabOrderFilters = {}) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.orders(filters as Record<string, unknown>), branchVersion],
    queryFn: () => lab.fetchLabOrders(filters),
    // Keeps the previous page on screen while the next loads, so paging a
    // work queue doesn't flash an empty table.
    placeholderData: (prev) => prev,
  });
}

export function useLabOrder(id: number | null) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.order(id ?? 0), branchVersion],
    queryFn: () => lab.fetchLabOrder(id as number),
    enabled: id !== null,
  });
}

export function useCreateLabOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLabOrderInput) => lab.createLabOrder(input),
    onSuccess: (order) => {
      invalidateWorkflow(qc, order.id);
      // The bill went through the existing engine, so the billing screens'
      // caches are now stale too — a lab charge is an ordinary bill and shows
      // up in the same lists.
      qc.invalidateQueries({ queryKey: keys.billing.list() });
      if (order.uhid) qc.invalidateQueries({ queryKey: keys.patientLab.history(order.uhid) });
    },
  });
}

export function useUpdateLabOrder(orderId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { priority?: string; clinical_notes?: string; diagnosis?: string }) =>
      lab.updateLabOrder(orderId, body),
    onSuccess: () => invalidateWorkflow(qc, orderId),
  });
}

export function useCancelLabOrder(orderId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { reason: string; itemIds?: number[] }) =>
      lab.cancelLabOrder(orderId, args.reason, args.itemIds),
    onSuccess: () => invalidateWorkflow(qc, orderId),
  });
}

/** TENANT-wide — no branchVersion, deliberately. */
export function usePatientLabHistory(uhid: string | null) {
  return useQuery({
    queryKey: keys.patientLab.history(uhid ?? ''),
    queryFn: () => lab.fetchPatientLabHistory(uhid as string),
    enabled: !!uhid,
  });
}

export function useParameterTrend(uhid: string | null, parameterCode: string | null) {
  return useQuery({
    queryKey: keys.patientLab.trend(uhid ?? '', parameterCode ?? ''),
    queryFn: () => lab.fetchParameterTrend(uhid as string, parameterCode as string),
    enabled: !!uhid && !!parameterCode,
  });
}

// ── Samples ──────────────────────────────────────────────────────

export function useLabSamples(filters: LabSampleFilters = {}) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.samples(filters as Record<string, unknown>), branchVersion],
    queryFn: () => lab.fetchLabSamples(filters),
    placeholderData: (prev) => prev,
  });
}

export function useLabSample(id: number | null) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.sample(id ?? 0), branchVersion],
    queryFn: () => lab.fetchLabSample(id as number),
    enabled: id !== null,
  });
}

/**
 * Barcode lookup. `enabled` gates it on a non-empty code so typing into the
 * scan box doesn't fire a request per keystroke — a handheld scanner types
 * the whole code then presses Enter, and the screen submits on that.
 */
export function useSampleByBarcode(barcode: string | null) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.sampleByBarcode(barcode ?? ''), branchVersion],
    queryFn: () => lab.fetchSampleByBarcode(barcode as string),
    enabled: !!barcode,
    retry: false,   // a 404 here means "not this branch's tube" — retrying says the same thing
  });
}

export function useCollectSample() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { id: number; orderId?: number; collection_location?: string; remarks?: string }) =>
      lab.collectSample(a.id, { collection_location: a.collection_location, remarks: a.remarks }),
    onSuccess: (_r, a) => { invalidateWorkflow(qc, a.orderId); qc.invalidateQueries({ queryKey: keys.laboratory.sample(a.id) }); },
  });
}

export function useReceiveSample() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { id: number; orderId?: number; accept?: boolean; remarks?: string }) =>
      lab.receiveSample(a.id, { accept: a.accept, remarks: a.remarks }),
    onSuccess: (_r, a) => { invalidateWorkflow(qc, a.orderId); qc.invalidateQueries({ queryKey: keys.laboratory.sample(a.id) }); },
  });
}

export function useRejectSample() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { id: number; orderId?: number; reason: RejectionReason; remarks?: string; require_recollection?: boolean }) =>
      lab.rejectSample(a.id, { reason: a.reason, remarks: a.remarks, require_recollection: a.require_recollection }),
    onSuccess: (_r, a) => { invalidateWorkflow(qc, a.orderId); qc.invalidateQueries({ queryKey: keys.laboratory.sample(a.id) }); },
  });
}

export function useRecollectSample() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { id: number; orderId?: number }) => lab.recollectSample(a.id),
    onSuccess: (_r, a) => invalidateWorkflow(qc, a.orderId),
  });
}

// ── Results ──────────────────────────────────────────────────────

export function useResultQueue(filters: ResultQueueFilters = {}) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.results(filters as Record<string, unknown>), branchVersion],
    queryFn: () => lab.fetchResultQueue(filters),
    placeholderData: (prev) => prev,
  });
}

export function useResultSheet(orderItemId: number | null) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.resultSheet(orderItemId ?? 0), branchVersion],
    queryFn: () => lab.fetchResultSheet(orderItemId as number),
    enabled: orderItemId !== null,
  });
}

export function useSaveResult() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { orderItemId: number; orderId?: number; input: SaveResultInput }) =>
      lab.saveResult(a.orderItemId, a.input),
    onSuccess: (sheet, a) => {
      // Write the server's answer straight into the cache: it carries the
      // flags and ranges it just computed, and refetching would show the
      // technician a moment of stale values on a screen they are still typing
      // into.
      qc.setQueryData([...keys.laboratory.resultSheet(a.orderItemId)], sheet);
      invalidateWorkflow(qc, a.orderId);
    },
  });
}

export function useVerifyResult() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { orderItemId: number; orderId?: number; decision: 'verified' | 'sent_back'; comments?: string }) =>
      lab.verifyResult(a.orderItemId, { decision: a.decision, comments: a.comments }),
    onSuccess: (_r, a) => {
      qc.invalidateQueries({ queryKey: keys.laboratory.resultSheet(a.orderItemId) });
      invalidateWorkflow(qc, a.orderId);
    },
  });
}

export function useReopenResult() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { orderItemId: number; orderId?: number; reason: string }) =>
      lab.reopenResult(a.orderItemId, a.reason),
    onSuccess: (_r, a) => {
      qc.invalidateQueries({ queryKey: keys.laboratory.resultSheet(a.orderItemId) });
      invalidateWorkflow(qc, a.orderId);
    },
  });
}

// ── Critical results ─────────────────────────────────────────────

export function useCriticalResults(params: { outstanding?: boolean } = {}) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.critical(params as Record<string, unknown>), branchVersion],
    queryFn: () => lab.fetchCriticalResults(params),
    refetchInterval: 60000,   // an unacknowledged critical value is time-sensitive
  });
}

export function useAcknowledgeCritical() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { id: number; notes?: string }) => lab.acknowledgeCritical(a.id, a.notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.laboratory.critical() });
      qc.invalidateQueries({ queryKey: keys.laboratory.dashboard() });
    },
  });
}

// ── Outside / reference laboratory ───────────────────────────────

/** Tenant-wide master — no branchVersion. */
export const useReferenceLabs = (includeInactive = false) =>
  useQuery({
    queryKey: [...keys.labMasters.referenceLabs(), includeInactive],
    queryFn: () => lab.fetchReferenceLabs(includeInactive),
    staleTime: 300000,
  });

export const useSaveReferenceLab = () =>
  useMasterMutation((a: { id?: number; body: Record<string, unknown> }) =>
    a.id ? lab.updateReferenceLab(a.id, a.body) : lab.createReferenceLab(a.body));

export function useOutsourcedWorklist(filters: lab.OutsourcedFilters = {}) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.outsourced(filters as Record<string, unknown>), branchVersion],
    queryFn: () => lab.fetchOutsourcedWorklist(filters),
    placeholderData: (prev) => prev,
  });
}

export function useOutsourcedItem(orderItemId: number | null) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.outsourcedItem(orderItemId ?? 0), branchVersion],
    queryFn: () => lab.fetchOutsourcedItem(orderItemId as number),
    enabled: orderItemId !== null,
  });
}

/** Tenant-wide — the patient's record. */
export function usePatientOutsourced(uhid: string | null) {
  return useQuery({
    queryKey: keys.patientLab.outsourced(uhid ?? ''),
    queryFn: () => lab.fetchPatientOutsourced(uhid as string),
    enabled: !!uhid,
  });
}

/**
 * Every outside-lab mutation invalidates the same set: the workflow queues,
 * the item's own detail, and — because the report is an ordinary patient
 * document and the result lands in the patient's record — that patient's
 * documents and laboratory history.
 */
function useOutsourcedMutation<TArgs extends { orderItemId?: number; orderId?: number; uhid?: string }, TResult>(
  fn: (args: TArgs) => Promise<TResult>
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_r, a) => {
      invalidateWorkflow(qc, a.orderId);
      if (a.orderItemId) qc.invalidateQueries({ queryKey: keys.laboratory.outsourcedItem(a.orderItemId) });
      if (a.uhid) {
        qc.invalidateQueries({ queryKey: keys.patientLab.outsourced(a.uhid) });
        qc.invalidateQueries({ queryKey: keys.patientLab.history(a.uhid) });
        qc.invalidateQueries({ queryKey: keys.patients.documents(a.uhid) });
      }
    },
  });
}

export const useDispatchOutsourcedSample = () =>
  useOutsourcedMutation((a: { sampleId: number; orderItemId?: number; orderId?: number; body: Parameters<typeof lab.dispatchOutsourcedSample>[1] }) =>
    lab.dispatchOutsourcedSample(a.sampleId, a.body));

export const useChangeSampleReferenceLab = () =>
  useOutsourcedMutation((a: { sampleId: number; orderItemId?: number; orderId?: number; reference_lab_id: number; reason: string }) =>
    lab.changeSampleReferenceLab(a.sampleId, { reference_lab_id: a.reference_lab_id, reason: a.reason }));

export const useUpdateOutsourcedStatus = () =>
  useOutsourcedMutation((a: { orderItemId: number; orderId?: number; body: Parameters<typeof lab.updateOutsourcedStatus>[1] }) =>
    lab.updateOutsourcedStatus(a.orderItemId, a.body));

export const useAttachOutsourcedReport = () =>
  useOutsourcedMutation((a: { orderItemId: number; orderId?: number; uhid?: string; input: lab.AttachReportInput }) =>
    lab.attachOutsourcedReport(a.orderItemId, a.input));

export const useSaveOutsourcedResult = () =>
  useOutsourcedMutation((a: { orderItemId: number; orderId?: number; uhid?: string; input: TranscriptionInput }) =>
    lab.saveOutsourcedResult(a.orderItemId, a.input));

export const useFinalizeOutsourced = () =>
  useOutsourcedMutation((a: { orderItemId: number; orderId?: number; uhid?: string }) =>
    lab.finalizeOutsourced(a.orderItemId));

/** OCR writes nothing to the result — it only refreshes the item (its OCR status). */
export function useRunOutsourcedOcr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { orderItemId: number }) => lab.runOutsourcedOcr(a.orderItemId),
    onSuccess: (_r, a) => { qc.invalidateQueries({ queryKey: keys.laboratory.outsourcedItem(a.orderItemId) }); },
  });
}

// ── Reports ──────────────────────────────────────────────────────

export function useLabReport(orderId: number | null) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.laboratory.report(orderId ?? 0), branchVersion],
    queryFn: () => lab.fetchLabReport(orderId as number),
    enabled: orderId !== null,
  });
}

export function useReleaseLabReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { orderId: number; uhid?: string }) => lab.releaseLabReport(a.orderId),
    onSuccess: (_r, a) => {
      invalidateWorkflow(qc, a.orderId);
      // The released PDF is now a patient_documents row, so the patient's
      // document list and laboratory history both changed.
      if (a.uhid) {
        qc.invalidateQueries({ queryKey: keys.patientLab.history(a.uhid) });
        qc.invalidateQueries({ queryKey: keys.patients.documents(a.uhid) });
      }
    },
  });
}

export function useAmendLabReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { orderId: number; reason: string; uhid?: string }) => lab.amendLabReport(a.orderId, a.reason),
    onSuccess: (_r, a) => {
      invalidateWorkflow(qc, a.orderId);
      if (a.uhid) {
        qc.invalidateQueries({ queryKey: keys.patientLab.history(a.uhid) });
        qc.invalidateQueries({ queryKey: keys.patients.documents(a.uhid) });
      }
    },
  });
}
